import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm } from '@mantine/form'
import { formatDate, passwordRules, roleCatalogOptions } from 'src/variable/constant'
import { Flex, Stack, Switch, TextInput } from '@mantine/core'
import CustomPasswordInput from 'src/components/CustomComponents/CustomInputPassword/CustomPasswordInput'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import md5 from 'md5'
import dayjs from 'dayjs'
import { createEmployee, updateEmployee } from 'src/apis/employeeAPI'
import CustomDatePickerInput from 'src/components/CustomComponents/CustomDatePickerInput'
import { useModal } from 'src/context/ModalContext'
import CustomModal from 'src/components/CustomComponents/CustomModal'
import CustomMultiSelect from 'src/components/CustomComponents/CustomMultiSelect/CustomMultiSelect'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'

interface ModalCreateEmployeeProps {
	open: boolean
	onClose: () => void
	handleGetData: () => void
	pagination: {
		pageIndex: number
		pageSize: number
	}
	handleChangePage: (_value: number) => void
	data?: any
	[key: string]: any
}

function ModalCreateEmployee({
	open,
	onClose,
	handleGetData,
	pagination,
	handleChangePage,
	data,
}: ModalCreateEmployeeProps) {
	// const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([])
	const accountTypeOptions = useMemo(() => [
		{ label: 'EMPLOYEE', value: 'EMPLOYEE' },
		{ label: 'SUPERADMIN', value: 'SUPERADMIN' },
	], [])
	const text = useMemo(() => (data?.id ? 'update' : 'create'), [data?.id])
	const form = useForm({
		initialValues: {
			name: '',
			accountType: 'EMPLOYEE',
			role: [] as string[],
			email: '',
			phone: '',
			password: '',
			confirmpassword: '',
			birthday: null,
			status: true,
		},

		validate: {
			name: (value) => {
				if (!value.trim()) return 'Name cannot be empty'
				if (value.trim().length < 2 || value.length > 50)
					return 'Name must be 2-50 characters long'
				return null
			},
			accountType: (value: string) => {
				if (!value) return 'Account type cannot be empty'
				return null
			},
			role: (value: string[]) => {
				if (form.values.accountType === 'SUPERADMIN') return null
				if (!value || value.length === 0) return 'Role cannot be empty'
				return null
			},
			email: (value) => {
				if (!value || !value.trim()) return 'Email cannot be empty'
				const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
				if (regex.test(value)) return null
				return 'Invalid Email'
			},
			password: (value) => {
				if (data?.id && !value.trim()) return null
				if (!value.trim()) return 'Password cannot be empty'
				if (!passwordRules.test(value)) {
					return 'Password must be 8-20 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character'
				}
				return null
			},
			confirmpassword: (value) => {
				if (data?.id && !form.values.password?.trim() && !value.trim()) return null
				if (!value.trim()) return 'Confirm password cannot be empty'
				if (value !== form.values.password) return 'Passwords did not match'
				return null
			},
			phone: (value) => {
				const phoneRegex = /^\+?\d+$/
				if (!value) return 'Phone cannot be empty'
				if (!phoneRegex.test(value)) return 'Invalid phone'
				if (value.length < 10 || value.length > 15)
					return 'Phone must be 10-15 characters long'
				return null
			},
		},
		validateInputOnBlur: true,
		validateInputOnChange: true,
	})
	const { openModal, closeModal, openError } = useModal()
	const handleSaveEmployee = useCallback(async ({ body, id = null }) => {
		try {
			const res = await (id ? updateEmployee(id, body) : createEmployee(body))
			if (res?.data?.code === 200) {
				if (id || pagination.pageIndex === 1) {
					await handleGetData()
				} else {
					await handleChangePage(1)
				}
				onClose()

				openModal({
					type: 'success',
					label: `Employee has been ${text} successfully`,
				})
			}
		} catch (error) {
			openError(error)
		}
	}, [text, pagination, handleGetData, handleChangePage, openModal, onClose, openError])
	const handleSubmit = useCallback(async (values) => {
		const normalizedPhone = String(values?.phone)?.startsWith('0')
			? '+84' + values?.phone.slice(1)
			: values?.phone
		const body: any = {
			name: values.name,
			email: values.email,
			phone: normalizedPhone,
			birthday: values.birthday ? dayjs(values.birthday).format('YYYY-MM-DD') : '',
			role: values.accountType,
			employee_roles: values.role,
			status: values.status,
		}

		if (data?.id) {
			if (values.password?.trim()) {
				body.password = md5(values.password)
			} else {
				delete body.password
			}
		} else {
			body.password = values.password?.trim() ? md5(values.password) : ''
		}

		openModal({
			type: 'confirm',
			label: `Do you want to ${text} this employee?`,
			handleClose: () => closeModal(),
			handleConfirm: () => {
				handleSaveEmployee({ body, id: data?.id || null })
				
			},
		})
	}, [text, data?.id, openModal, closeModal, handleSaveEmployee])
	// useEffect(() => {
	// 	const handleGetRoles = async () => {
	// 		try {
	// 			const res = await getListRole({
	// 				fields: ['$all'],
	// 				page: 1,
	// 				limit: 100,
	// 			})
	// 			const options =
	// 				res?.data?.results?.objects?.rows?.map((item) => ({
	// 					label: item.name,
	// 					value: item.name,
	// 				})) || []
	// 			setRoleOptions(options)
	// 		} catch (error) {
	// 			openError(error)
	// 		}
	// 	}

	// 	handleGetRoles()
	// }, [openError])
	useEffect(() => {
		if (!open) return

		if (data?.id) {
			const rawPermissions = data?.employee_roles ?? (Array.isArray(data?.role) ? data?.role : [])
			const roleValue: string[] = Array.isArray(rawPermissions)
				? rawPermissions.map((r: any) => (typeof r === 'string' ? r : r?.name || ''))
				: rawPermissions ? [typeof rawPermissions === 'string' ? rawPermissions : rawPermissions?.name || ''] : []
			const accountType = typeof data?.role === 'string' ? data?.role : 'EMPLOYEE'
			form.setValues({
				name: data?.name || '',
				accountType,
				role: roleValue,
				email: data?.email || '',
				phone: data?.phone || '',
				password: '',
				confirmpassword: '',
				birthday: data?.birthday ? dayjs(Number(data.birthday) || data.birthday).toDate() : null,
				status: typeof data?.status === 'boolean' ? data.status : true,
			})
			return
		}

		form.reset()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, data?.id])
	return (
		<CustomModal
			// size="auto"

			opened={open}
			onClose={onClose}
		>
			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Stack spacing="0.75rem">
					<Flex sx={{ fontSize: 25, fontWeight: 700 }} justify="center">
						{text === 'create' ? 'Create Employee' : 'Update Employee'}
					</Flex>
					<TextInput
						autoComplete="off"
						withAsterisk
						key="name"
						label={'Name'}
						name={'name'}
						placeholder={"Employee's name"}
						maxLength={50}
						{...form.getInputProps('name')}
					/>				<TextInput
					autoComplete="off"
					withAsterisk
					key="email"
					label={'Email'}
					name={'email'}
					placeholder={"Employee's email"}
					maxLength={50}
					{...form.getInputProps('email')}
				/>					<CustomSelect
						withAsterisk
						label={'Account type'}
						placeholder={'Select account type'}
						data={accountTypeOptions}
						value={form.values.accountType}
						onChange={(value) => form.setFieldValue('accountType', value || 'EMPLOYEE')}
						error={form.errors.accountType as string}
					/>
					{form.values.accountType !== 'SUPERADMIN' && (
						<CustomMultiSelect
							withAsterisk
							label={'Permissions'}
							placeholder={"Employee's permissions"}
							// data={roleOptions}
							data={roleCatalogOptions}
							searchable
							value={form.values.role}
							onChange={(value) => form.setFieldValue('role', value)}
							error={form.errors.role as string}
						/>
					)}
					<TextInput
						autoComplete="new-password"
						withAsterisk
						key="phone"
						label={'Phone'}
						name={'phone'}
						placeholder={"Employee's Phone"}
						maxLength={50}
						{...form.getInputProps('phone')}
					/>

					<CustomPasswordInput
						autoComplete="new-password"
						withAsterisk={!data?.id}
						key="password"
						label={'Password'}
						name={'password'}
						placeholder={"Employee's password"}
						maxLength={50}
						{...form.getInputProps('password')}
					/>
					<CustomPasswordInput
						autoComplete="new-password"
						withAsterisk={!data?.id}
						key="confirmpassword"
						label={'Confirm password'}
						name={'confirmpassword'}
						placeholder={"Confirm employee's password"}
						maxLength={50}
						{...form.getInputProps('confirmpassword')}
					/>
					<CustomDatePickerInput
						key={'birthday'}
						label={'Birthday'}
						name={'birthday'}
						valueFormat={formatDate}
						defaultValue={null}
						maxDate={new Date()}
						{...form.getInputProps('birthday')}
					/>				<Flex align="center" gap={8}>
					<label style={{ fontWeight: 500 }}>Status</label>
					<Switch
						checked={form.values.status}
						onChange={(e) => form.setFieldValue('status', e.currentTarget.checked)}
						label={form.values.status ? 'Active' : 'Inactive'}
					/>
				</Flex>					<CustomButton variant="outlined" type="submit">
						{text === 'create' ? 'Create' : 'Update'}
					</CustomButton>
				</Stack>
			</form>
		</CustomModal>
	)
}

export default React.memo(ModalCreateEmployee)
